import { Box, CircularProgress, Typography } from '@material-ui/core';
import { useDocumentStore } from '@roc/feature-documents';
import { Document } from '@roc/feature-types';
import { Button, StandardDialog } from '@roc/ui';
import { observer } from 'mobx-react';
import { useEffect } from 'react';

export type LoanFastTrackPaymentDialogProps = {
  loanId?: string;
  open?: boolean;
  onClose?: () => void;
  document: Document;
};

/**
 * The borrower portal's Fast Track payment task. The payment itself happens on the Fast Track secure link page
 * (/link/loan-fastrack-payment), the same one the lender emails: the backend checks that the borrower is on the loan
 * and returns the open link (or a new one), which opens in a new tab.
 */
export const BorrowerLoanFastTrackPaymentDialog = observer(
  (props: LoanFastTrackPaymentDialogProps) => {
    const { loanId, open, onClose } = props;
    const { documentStore } = useDocumentStore();
    const { documentFormStore } = documentStore;
    const { loanFastrackPaymentStore } = documentFormStore;
    const { loanFastTrackInfo, fastTrackPaymentUrl } = loanFastrackPaymentStore;

    useEffect(() => {
      if (open && loanId) {
        // Loads the Fast Track, then its payment link.
        loanFastrackPaymentStore.loadFastTrackInfo(loanId);
      }
      return () => {
        loanFastrackPaymentStore.reset();
      };
    }, [loanId, open]);

    const content = () => {
      if (loanFastTrackInfo?.paymentStatus) {
        return <Typography>The Fast Track fee for this loan has been paid. Thank you!</Typography>;
      }
      if (!fastTrackPaymentUrl) {
        return (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        );
      }
      return (
        <>
          <Typography gutterBottom>
            The Fast Track payment opens in a new tab, on a secure page for this loan only.
          </Typography>
          <Box mt={3} display="flex" justifyContent="center">
            <Button
              testId="openFastTrackPayment"
              variant="contained"
              color="primary"
              onClick={() => window.open(fastTrackPaymentUrl, '_blank', 'noopener,noreferrer')}
            >
              Pay Fast Track fee
            </Button>
          </Box>
        </>
      );
    };

    // Nothing to pay until the loan's Fast Track has loaded (as before, when the steps were rendered here).
    if (!loanFastTrackInfo?.randomLoanId) {
      return null;
    }

    return (
      <StandardDialog
        open={open}
        title="Loan Fast Track Payment"
        maxWidth="sm"
        handleClose={onClose}
        dialogContent={<Box width={'100%'} py={2}>{content()}</Box>}
      />
    );
  }
);
